<script>
  import Story from "./Story.svelte";
  import { onMount, onDestroy } from "svelte";
  import { projectArray, getNext, getPrev } from "../stores.js";
  import { push } from "svelte-spa-router";

  let activeStories = [];
  for (const project of projectArray) {
    activeStories.push(0);
  }

  export let params = { project: 0, story: 0 };

  // when params change, update activeStories
  // these $ variables recompute when stuff changes
  $: activeStories[parseInt(params.project)] = parseInt(params.story);

  $: next = getNext(params);
  $: prev = getPrev(params);
  $: nextProject =
    parseInt(params.project) < projectArray.length - 1
      ? parseInt(params.project) + 1
      : 0;
  $: prevProject =
    parseInt(params.project) > 0
      ? parseInt(params.project) - 1
      : projectArray.length - 1;

  // Detect if current section is landscape
  $: currentIsLandscape = projectArray[parseInt(params.project)]?.isLandscape || false;
  
  // Base dimensions for portrait and landscape
  const PORTRAIT_WIDTH = 414;
  const PORTRAIT_HEIGHT = 736;
  const LANDSCAPE_WIDTH = 736;
  const LANDSCAPE_HEIGHT = 414;
  
  // Reactive dimensions based on current section orientation
  $: baseWidth = currentIsLandscape ? LANDSCAPE_WIDTH : PORTRAIT_WIDTH;
  $: baseHeight = currentIsLandscape ? LANDSCAPE_HEIGHT : PORTRAIT_HEIGHT;

  let bufferProject = 0;

  let gesture_start,
    gesture_active,
    gesture_gap = 0;

  let held = false;
  let swipeDirection = "right";
  const swipeSensitivity = 100;

  let gestureTimedOut = true; //whether a gesture has timed out
  let gestureTimer; //timer object to time that

  let storyTimer; //timer object to time stories
  const storyTimerTime = 6000; //time limit for stories, ms

  let navOpen = false;
  let isPaused = false;
  
  // ============================================
  // BOTTOM SHEET CONFIGURATION
  // ============================================
  // Each item is a video/image with an optional caption
  // - 8px gap between media and its caption
  // - 32px gap between items
  //
  // Example:
  // {
  //   id: "feature-demo",
  //   title: "Feature Demo",
  //   items: [
  //     { type: "video", src: "./videos/feature.mp4", caption: "How it works" },
  //     { type: "image", src: "./images/screenshot.png", caption: "The result" },
  //   ]
  // }
  // ============================================
  
  const bottomSheetConfig = [
    {
      id: "start_analysis",
      title: "New Analysis",
      items: [
        { type: "video", src: "./videos/starting-a-new-exploration.mp4", caption: "Starting a new exploration" },
        { type: "video", src: "./videos/editing-a-question-fixed.mp4", caption: "Editing a question" },
        { type: "video", src: "./videos/semantic-chip-interaction.mp4", caption: "Updating the interpreted metric" },
      ]
    },

    {
      id: "chart_shortcut",
      title: "Chart Shortcuts",
      items: [
        { type: "video", src: "./videos/fsc-shortcut.mp4", caption: "Shift C for quick metric lookup" },
      ]
    },

    {
      id: "new_vis",
      title: "New Visualizations",
      items: [
        { type: "image", src: "./videos/new-vis-1.jpg", caption: "Bar chart" },
        { type: "image", src: "./videos/new-vis-2.jpg", caption: "Size by in scatter plot" },
        { type: "image", src: "./videos/new-vis-3.jpg", caption: "Breakdowns in scatter plot" },
      ]
    },

    {
      id: "event_analytics",
      title: "Event Analytics",
      items: [
        { type: "video", src: "./videos/event-aggregations.mp4", caption: "Event aggregations" },
        { type: "video", src: "./videos/event-filter.mp4", caption: "Filtering events" },
        { type: "video", src: "./videos/event-global-filters.mp4", caption: "Global filters" },
        { type: "video", src: "./videos/custom-events.mp4", caption: "Creating a custom event" },
      ]
    },
  ];

  // Bottom sheet state
  let bottomSheetOpen = false;
  let activeSheet = null;
  let sheetVideoElements = [];
  let videoProgress = [];
  let videoPlaying = [];
  let progressAnimationFrames = [];
  let controlsVisible = [];
  let sheetLoading = true;
  let loadedAssets = 0;
  let controlsHideTimers = [];

  function showControls(index) {
    controlsVisible[index] = true;
    controlsVisible = [...controlsVisible];
    resetControlsTimer(index);
  }

  function hideControls(index) {
    controlsVisible[index] = false;
    controlsVisible = [...controlsVisible];
  }

  function resetControlsTimer(index) {
    if (controlsHideTimers[index]) {
      clearTimeout(controlsHideTimers[index]);
    }
    controlsHideTimers[index] = setTimeout(() => {
      hideControls(index);
    }, 2500);
  }

  function handleVideoMouseMove(index) {
    showControls(index);
  }

  function handleVideoMouseLeave(index) {
    if (controlsHideTimers[index]) {
      clearTimeout(controlsHideTimers[index]);
    }
    hideControls(index);
  }

  function openBottomSheet(sheetId) {
    const sheet = bottomSheetConfig.find(s => s.id === sheetId);
    if (sheet) {
      activeSheet = sheet;
      bottomSheetOpen = true;
      sheetLoading = true;
      loadedAssets = 0;
      storyTimer.pause();
    }
  }

  function onAssetLoad() {
    loadedAssets++;
    if (activeSheet && loadedAssets >= activeSheet.items.length) {
      sheetLoading = false;
    }
  }

  function closeBottomSheet() {
    // Cancel all animation frames
    progressAnimationFrames.forEach(frameId => {
      if (frameId) cancelAnimationFrame(frameId);
    });
    // Pause all videos
    sheetVideoElements.forEach(el => {
      if (el) {
        el.pause();
        el.currentTime = 0;
      }
    });
    bottomSheetOpen = false;
    activeSheet = null;
    sheetVideoElements = [];
    videoProgress = [];
    videoPlaying = [];
    progressAnimationFrames = [];
    controlsHideTimers.forEach(timer => clearTimeout(timer));
    controlsVisible = [];
    controlsHideTimers = [];
    if (!isPaused) {
      storyTimer.resume();
    }
  }

  // Custom video controls
  function toggleVideoPlayPause(index) {
    const video = sheetVideoElements[index];
    if (video) {
      if (video.paused) {
        video.play();
        videoPlaying[index] = true;
      } else {
        video.pause();
        videoPlaying[index] = false;
      }
      videoPlaying = [...videoPlaying]; // trigger reactivity
    }
  }

  function startProgressAnimation(index) {
    function animate() {
      const video = sheetVideoElements[index];
      if (video && video.duration && !video.paused) {
        videoProgress[index] = (video.currentTime / video.duration) * 100;
        videoProgress = [...videoProgress];
        progressAnimationFrames[index] = requestAnimationFrame(animate);
      }
    }
    animate();
  }

  function stopProgressAnimation(index) {
    if (progressAnimationFrames[index]) {
      cancelAnimationFrame(progressAnimationFrames[index]);
      progressAnimationFrames[index] = null;
    }
  }

  function updateVideoProgressOnce(index) {
    const video = sheetVideoElements[index];
    if (video && video.duration) {
      videoProgress[index] = (video.currentTime / video.duration) * 100;
      videoProgress = [...videoProgress];
    }
  }

  function seekVideo(index, event) {
    const video = sheetVideoElements[index];
    const progressBar = event.currentTarget;
    if (video && progressBar) {
      const rect = progressBar.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const percentage = clickX / rect.width;
      video.currentTime = percentage * video.duration;
    }
  }

  function toggleFullscreen(index) {
    const video = sheetVideoElements[index];
    if (!video) return;

    // Check if we're on iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    
    // Check if already in fullscreen
    const isFullscreen = document.fullscreenElement || 
                         document.webkitFullscreenElement || 
                         document.mozFullScreenElement ||
                         document.msFullscreenElement ||
                         video.webkitDisplayingFullscreen;

    if (isFullscreen) {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      } else if (video.webkitExitFullscreen) {
        video.webkitExitFullscreen();
      }
    } else {
      // Enter fullscreen
      if (isIOS) {
        // iOS Safari: must use webkitEnterFullscreen on the video element
        // Video must be playing for this to work
        if (video.webkitEnterFullscreen) {
          if (video.paused) {
            video.play().then(() => {
              video.webkitEnterFullscreen();
            }).catch(() => {
              // If autoplay fails, try fullscreen anyway
              video.webkitEnterFullscreen();
            });
          } else {
            video.webkitEnterFullscreen();
          }
        }
      } else {
        // Standard browsers
        if (video.requestFullscreen) {
          video.requestFullscreen();
        } else if (video.webkitRequestFullscreen) {
          video.webkitRequestFullscreen();
        } else if (video.mozRequestFullScreen) {
          video.mozRequestFullScreen();
        } else if (video.msRequestFullscreen) {
          video.msRequestFullscreen();
        }
      }
    }
  }

  function togglePause() {
    if (isPaused) {
      storyTimer.resume();
      isPaused = false;
    } else {
      storyTimer.pause();
      isPaused = true;
    }
  }

  function Timer(callback, delay) {
    var timerId,
      start,
      delayStore = delay,
      remaining = delay;

    this.pause = function() {
      window.clearTimeout(timerId);
      remaining -= Date.now() - start;
    };

    this.clear = function() {
      window.clearTimeout(timerId);
    };

    this.resume = function() {
      start = Date.now();
      window.clearTimeout(timerId);
      timerId = window.setTimeout(callback, remaining);
    };

    this.reset = function() {
      start = Date.now();
      remaining = delayStore;
      window.clearTimeout(timerId);
      timerId = window.setTimeout(callback, remaining);
    };

    this.resume();
  }

  function handleNavProject(event) {
    pushHandler(event.detail, 0);
    storyTimer.resume();
  }

  //if the project has changed since router push went through
  $: if (parseInt(params.project) != bufferProject) {
    if (parseInt(params.project) == 0) {
      bufferProject == projectArray.length - 1
        ? (swipeDirection = "left")
        : (swipeDirection = "right");
    } else if (parseInt(params.project) == projectArray.length - 1) {
      bufferProject == 0
        ? (swipeDirection = "right")
        : (swipeDirection = "left");
    } else {
      parseInt(params.project) > bufferProject
        ? (swipeDirection = "left")
        : (swipeDirection = "right");
    }
    bufferProject = parseInt(params.project);
  }

  function pushHandler(project, story) {
    //s omething in this router
    // console.log("pushHandler :" + project + ", " + story);
    push(`/${project.toString()}/${story.toString()}`); //push to router
    if (storyTimer) storyTimer.reset();
  }

  // Animated navigation for multi-step jumps
  let isAnimating = false;
  
  function animatedNavTo(targetProject) {
    const currentProject = parseInt(params.project);
    if (targetProject === currentProject || isAnimating) return;
    
    isAnimating = true;
    const direction = targetProject > currentProject ? 1 : -1;
    const steps = Math.abs(targetProject - currentProject);
    let currentStep = 0;
    
    function animateStep() {
      currentStep++;
      const nextProj = currentProject + (direction * currentStep);
      pushHandler(nextProj, currentStep === steps ? 0 : activeStories[nextProj] || 0);
      
      if (currentStep < steps) {
        setTimeout(animateStep, 300); // 400ms between each step
      } else {
        isAnimating = false;
      }
    }
    
    animateStep();
  }

  function handleProjects(direction) {
    if (direction == "next") {
      pushHandler(next.project, next.story);
    } else if (direction == "prev") {
      pushHandler(prev.project, prev.story);
    } else if (direction == "nextProject") {
      pushHandler(nextProject, activeStories[nextProject]);
    } else if (direction == "prevProject") {
      pushHandler(prevProject, activeStories[prevProject]);
    }
  }

  function gestureDown(e) {
    //when a gesture starts
    // If clicking on a link, don't start gesture (let link handler take over)
    if (e.target.closest('a')) {
      return;
    }
    
    if (navOpen) {
      navOpen = false; //close nav
      storyTimer.resume(); //pause story timer
    } else {
      storyTimer.pause(); //pause story timer
      // navOpen = false; //close nav
      held = true; //start holding gesture
      gestureTimedOut = false; //reset gestureTimedOut, hasn't timed out yet

      e.type == "touchstart"
        ? (gesture_start = Math.round(e.changedTouches[0].pageX)) //where the event starts
        : (gesture_start = e.pageX);

      gesture_active = gesture_start;

      gestureTimer = setTimeout(() => {
        gestureTimedOut = true; //start gesture timer
      }, 300);
    }
  }

  function gestureMove(e) {
    //when ya movin
    e.type == "touchmove"
      ? (gesture_active = Math.round(e.changedTouches[0].pageX))
      : (gesture_active = e.pageX);

    gesture_gap = gesture_active - gesture_start; //set the gap between start and where you've dragged
    gesture_gap > 0 ? (swipeDirection = "left") : (swipeDirection = "right");
  }

  function gestureUp(e) {
    storyTimer.resume();
    if (!gestureTimedOut) {
      //if the gesture hasn't timed out
      if (gesture_active > gesture_start + swipeSensitivity) {
        handleProjects("prevProject");
      } else if (gesture_active < gesture_start - swipeSensitivity) {
        handleProjects("nextProject");
      } else {
        //JUST GO NEXT OR PREV
        if (Math.abs(gesture_gap) < 10) {
          gesture_active > window.innerWidth / 2
            ? handleProjects("next")
            : handleProjects("prev");
        }
      }
    } else {
      //if the gesture has timed out
      if (gesture_active > gesture_start + 200) {
        handleProjects("prevProject");
      } else if (gesture_active < gesture_start - 200) {
        handleProjects("nextProject");
      }
      //reset stuff
    }
    // if (storyTimer) storyTimer.reset();
    if (gestureTimer) clearTimeout(gestureTimer);
    held = false; //end holding gesture
    gesture_start = 0;
    gesture_active = 0;
    gesture_gap = 0;
  }

  function handleKeydown(e) {
    if (e.keyCode == 39 || e.keyCode == 32 || e.keyCode == 68) {
      handleProjects("next");
    } else if (e.keyCode == 37 || e.keyCode == 8 || e.keyCode == 65) {
      handleProjects("prev");
    }
  }

  // Scale factor for larger viewports
  let scaleFactor = 1;

  // Calculate scale factor - Instagram-like scaling
  function updateScaleFactor() {
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    
    // Only scale UP on larger viewports (desktop)
    // Mobile stays at scale=1, CSS handles fitting naturally
    if (vh > 600 && vw > 600) {
      if (currentIsLandscape) {
        // Landscape: scale based on width being the limiting factor
        const targetWidth = vw * 0.9;
        const targetHeight = vh * 0.9;
        scaleFactor = Math.min(targetWidth / LANDSCAPE_WIDTH, targetHeight / LANDSCAPE_HEIGHT, 2);
      } else {
        // Portrait: scale based on height
        const targetHeight = vh * 0.96;
        scaleFactor = Math.min(targetHeight / PORTRAIT_HEIGHT, 2);
      }
    } else {
      scaleFactor = 1;
    }
  }
  
  // Re-calculate scale when orientation changes
  $: if (currentIsLandscape !== undefined) {
    updateScaleFactor();
  }

  onMount(() => {
    //when first mounts; basically on page load
    storyTimer = new Timer(() => {
      handleProjects("next");
    }, storyTimerTime);

    // Open all links in new tab
    function handleLinkClick(e) {
      const target = e.target.closest('a');
      if (!target) return;
      if (target.href && !target.href.startsWith('javascript:')) {
        e.preventDefault();
        e.stopPropagation();
        window.open(target.href, '_blank', 'noopener,noreferrer');
      }
    }

    document.addEventListener("click", handleLinkClick, true);
    document.addEventListener("touchend", handleLinkClick, true);

    // Handle bottom sheet triggers - elements with data-sheet-id
    function handleSheetTrigger(e) {
      const trigger = e.target.closest('[data-sheet-id]');
      if (trigger) {
        e.preventDefault();
        e.stopPropagation();
        // Only open on click/touchend, not on mousedown/touchstart
        if (e.type === 'click' || e.type === 'touchend') {
          const sheetId = trigger.dataset.sheetId;
          if (sheetId) {
            openBottomSheet(sheetId);
          }
        }
      }
    }
    document.addEventListener("click", handleSheetTrigger, true);
    document.addEventListener("mousedown", handleSheetTrigger, true);
    document.addEventListener("mouseup", handleSheetTrigger, true);
    document.addEventListener("touchstart", handleSheetTrigger, true);
    document.addEventListener("touchend", handleSheetTrigger, true);

    // Set up responsive scaling
    updateScaleFactor();
    window.addEventListener('resize', updateScaleFactor);

    return () => {
      window.removeEventListener('resize', updateScaleFactor);
      document.removeEventListener('click', handleSheetTrigger, true);
      document.removeEventListener('mousedown', handleSheetTrigger, true);
      document.removeEventListener('mouseup', handleSheetTrigger, true);
      document.removeEventListener('touchstart', handleSheetTrigger, true);
      document.removeEventListener('touchend', handleSheetTrigger, true);
    };
  });

  onDestroy(() => {
    storyTimer.clear();
    storyTimer = null;
  });
</script>

<style>
  :root {
    --width-border: 414px;
    --height-border: 736px;
  }

  .nav-container {
    position: absolute;
    top: 12px;
    left: 12px;
    padding: 8px 12px;
    /* background-color: #3B5998; */
    font-family: Verdana, sans-serif;
    font-size: 12px;
    color: white;
    z-index: 9999;
    border-radius: 4px;
    cursor: pointer;
  }

  .nav-container:hover {
    background-color: #2d4373;
  }

  .nav-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .nav-item {
    padding: 4px 0;
    opacity: 0.7;
    transition: opacity 0.2s;
  }

  .nav-item button {
    background: none;
    border: none;
    color: inherit;
    font: inherit;
    cursor: pointer;
    padding: 0;
  }

  .nav-item:hover {
    opacity: 1;
  }

  .nav-item.active {
    opacity: 1;
  }

  .nav-item.hidden {
    display: none;
  }

  @media screen and (max-width: 800px) {
    .nav-container {
      display: none;
    }
  }

  .grabbing {
    cursor: grabbing !important;
  }

  main {
    height: calc(100vh - 30px);
    position: relative;
    width: 100vw;
    padding: 0;
    margin: 0;
    transform-style: preserve-3d;
    will-change: transform, max-width, max-height;
  }

  main.landscape {
    /* Landscape orientation */
    aspect-ratio: 16 / 9;
  }

  @media screen and (max-width: 550px) {
    /* Mobile portrait */
    main.portrait {
      border-radius: 0px;
      margin: 0;
      height: 100vh;
      max-height: 100vh !important;
      max-width: 100vw !important;
    }
  }

  @media screen and (max-width: 800px) {
    /* Mobile landscape */
    main.landscape {
      border-radius: 0px;
      margin: 0;
      height: 100vh;
      max-height: 100vh !important;
      max-width: 100vw !important;
    }
  }

  .project {
    /* max-width: 460px; */
    width: 100%;
    height: 100%;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    transition: left 0.5s ease, transform 0.5s ease;
    display: none;
    overflow: hidden;
    background: transparent;
    will-change: transform;
  }

  .prevProject {
    display: block;
    transform: translateX(-100%) rotateY(-90deg);
    -webkit-transform: translateX(-100%) rotateY(-90deg);
    z-index: -1;
    position: absolute;
    top: 0;
    /* left: -100%; */
  }

  .currentProject {
    display: block;
    transform: translateX(0%) rotateY(0deg);
    -webkit-transform: translateX(0%) rotateY(0deg);
    position: absolute;
    top: 0;
    left: 0%;
  }

  .nextProject {
    display: block;
    z-index: -1;
    position: absolute;
    top: 0;
    transform: translateX(100%) rotateY(90deg);
    -webkit-transform: translateX(100%) rotateY(90deg);
    /* left: 100%; */
  }

  #indicators {
    height: 1.5px;
    top: 12px;
    left: 12px;
    right: 12px;
    z-index: 1;
    position: absolute;
    display: flex;
    justify-content: space-between;
    align-items: stretch;
  }

  .play-pause-btn {
    position: absolute;
    top: 21.5px; /* 12px (progress bar top) + 1.5px (height) + 8px gap */
    right: 12px;
    width: 20px;
    height: 20px;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .play-pause-btn svg {
    width: 20px;
    height: 20px;
  }

  .profile-info {
    position: absolute;
    top: 21.5px; /* same as play-pause button */
    left: 12px;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    z-index: 2;
    text-decoration: none;
    cursor: pointer;
  }

  .profile-pic {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    object-fit: cover;
  }

  .profile-name-badge {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
  }

  .profile-name {
    font-family: Verdana, sans-serif;
    font-size: 12px;
    color: white;
  }

  .verified-badge {
    width: 12px;
    height: 12px;
  }

  /* Video Bottom Sheet Styles */
  .video-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0);
    z-index: 10000;
    animation: fadeIn 0.3s ease forwards;
  }

  @keyframes fadeIn {
    to { background: rgba(0, 0, 0, 0.1); }
  }

  .video-bottomsheet {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 50%;
    background: #142542;
    box-sizing: border-box;
    width: 100%;
    max-width: 1200px;
    transform: translateX(-50%) scale(0.96);
    transform-origin: center center;
    opacity: 0;
    filter: blur(8px);
    animation: appearIn 0.3s ease-out forwards;
    box-shadow: 0 -4px 40px rgba(0, 0, 0, 0.4);
    font-family: "Work Sans", sans-serif;
    will-change: opacity, transform, filter;
    display: flex;
    flex-direction: column;
  }

  @keyframes appearIn {
    to { 
      opacity: 1;
      transform: translateX(-50%) scale(1);
      filter: blur(0px);
    }
  }

  .bottomsheet-header {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding: 16px 8px;
    flex-shrink: 0;
  }

  .bottomsheet-back {
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 4px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s ease;
  }

  .bottomsheet-back:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  .bottomsheet-back svg path {
    stroke: rgba(255, 255, 255, 0.8);
  }

  .sheet-content {
    display: flex;
    flex-direction: column;
    gap: 48px;
    flex: 1;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: rgba(255,255,255,0.2) transparent;
    padding: 8px 20px 24px 20px;
  }

  .sheet-item {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .sheet-content::-webkit-scrollbar {
    width: 3px;
  }

  .sheet-content::-webkit-scrollbar-track {
    background: transparent;
  }

  .sheet-content::-webkit-scrollbar-thumb {
    background: rgba(255,255,255,0.2);
    border-radius: 0;
  }

  .sheet-content.single-item {
    justify-content: center;
  }

  .sheet-content.loading {
    opacity: 0;
    pointer-events: none;
  }

  .sheet-loading {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .loading-spinner {
    width: 24px;
    height: 24px;
    border: 2px solid rgba(255, 255, 255, 0.2);
    border-top-color: rgba(255, 255, 255, 0.8);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .sheet-video {
    width: 100%;
    height: auto;
    background: #000;
    flex-shrink: 0;
    display: block;
  }

  .video-container {
    position: relative;
    overflow: hidden;
  }

  .video-controls {
    position: absolute;
    bottom: 0;
    opacity: 1;
    transition: opacity 0.3s ease;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.6) 0%, transparent 100%);
  }

  .video-controls.hidden {
    opacity: 0;
  }

  .video-play-btn,
  .video-fullscreen-btn {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .video-play-btn svg,
  .video-fullscreen-btn svg {
    width: 20px;
    height: 20px;
  }

  .video-progress {
    flex: 1;
    height: 3px;
    background: rgba(255, 255, 255, 0.3);
    cursor: pointer;
    position: relative;
  }

  .video-progress-bar {
    height: 100%;
    background: rgba(255, 255, 255, 1);
  }

  .sheet-item-header {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .sheet-number {
    color: rgba(255, 255, 255, 0.2);
    font-feature-settings: 'liga' off, 'clig' off;
    font-family: "Work Sans", sans-serif;
    font-size: 24px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
  }

  .sheet-caption {
    font-family: "Work Sans", sans-serif;
    font-size: 14px;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.5;
    margin: 0;
  }

  .sheet-image {
    width: 100%;
    height: auto;
    border-radius: 0;
    flex-shrink: 0;
  }

  /* Mobile bottomsheet styles */
  @media screen and (max-width: 550px) {
    .bottomsheet-header {
      padding: 8px 4px;
    }
    
    .bottomsheet-back svg {
      width: 24px;
      height: 24px;
    }
    
    .sheet-content {
      padding: 16px;
    }
  }

  #indicators div {
    background-color: white;
    height: 100%;
    flex-grow: 1;
    border-radius: 2px;
    margin: 0 2px;
  }
  .nextIndicators {
    opacity: 0.5;
  }

  #indicators #currentIndicator {
    background-color: rgba(255, 255, 255, 0.5);
    position: relative;
    overflow: hidden;
  }

  #indicators div #loadingBar {
    position: absolute;
    top: 0px;
    left: 0px;
    margin: 0px;
    height: 100%;
    background: white;
    animation: progress 6s linear;
    -moz-animation: progress 6s linear;
    -o-animation: progress 6s linear;

    animation-name: progress;
    animation-duration: 6s;
    animation-timing-function: linear;
    animation-fill-mode: forwards;

    -webkit-animation-name: progress;
    -webkit-animation-duration: 6s;
    -webkit-animation-timing-function: linear;
    -webkit-animation-fill-mode: forwards;
  }

  .paused {
    -webkit-animation-play-state: paused !important;
    -moz-animation-play-state: paused !important;
    -o-animation-play-state: paused !important;
    animation-play-state: paused !important;
  }

  @keyframes -global-progress {
    0% {
      width: 0%;
    }
    100% {
      width: 100%;
    }
  }
  @-webkit-keyframes -global-progress {
    0% {
      width: 0%;
    }
    100% {
      width: 100%;
    }
  }
  @-moz-keyframes -global-progress {
    0% {
      width: 0%;
    }
    100% {
      width: 100%;
    }
  }
  @-o-keyframes -global-progress {
    0% {
      width: 0%;
    }
    100% {
      width: 100%;
    }
  }

  </style>

<svelte:window on:keydown={handleKeydown} />
<svelte:options immutable={true} />

<nav 
  class="nav-container"
  on:mouseenter={() => { navOpen = true; storyTimer.pause(); }}
  on:mouseleave={() => { navOpen = false; storyTimer.resume(); }}
>
  <ul class="nav-list">
    {#each projectArray as project, i}
      <li class="nav-item {params.project == i ? 'active' : ''} {!navOpen && params.project != i ? 'hidden' : ''}">
        <button 
          type="button"
          on:click={() => { animatedNavTo(i); navOpen = false; }}
        >
          {project.name}
        </button>
      </li>
    {/each}
  </ul>
</nav>

<!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
<div
  role="application"
  aria-label="Story viewer - swipe or use arrow keys to navigate"
  style="overflow: hidden; height: 100vh; width: 100vw; display: flex;
  align-items: center; justify-content: center; perspective: 1080px; cursor:
  ew-resize"
  on:mousedown={gestureDown}
  on:mousemove={e => {
    if (held) gestureMove(e);
  }}
  on:mouseup={e => {
    if (held) gestureUp(e);
  }}
  on:touchstart|preventDefault={gestureDown}
  on:touchmove|passive={e => {
    if (held) gestureMove(e);
  }}
  on:touchend|preventDefault={e => {
    if (held) gestureUp(e);
  }}
  class={held ? 'grabbing' : 'no'}>

  <main
    style="transform: scale({scaleFactor}) translateX({held ? Math.max(Math.min(gesture_gap * 1.1, baseWidth), -baseWidth) : 0}px);
    max-width: {baseWidth}px; max-height: {baseHeight}px;
    {held ? 'transition: transform 0s;' : 'transition: transform .5s ease, max-width .5s ease, max-height .5s ease;'}"
    class={currentIsLandscape ? 'landscape' : 'portrait'}>

    {#each projectArray as { name, stories }, i}
      <!-- each project -->

      <div
        class="project {params.project == i ? 'currentProject ' : ''}{i == nextProject ? 'nextProject ' : ''}{i == prevProject ? 'prevProject ' : ''}"
        style="{held && (i == params.project || i == nextProject || i == prevProject) ? 'transform: translateX(' + (i == prevProject ? -100 : i == nextProject ? 100 : 0) + '%) rotateY(' + (Math.min(Math.max(gesture_gap / 4.2, -90), 90) + (i == nextProject ? 90 : 0) + (i == prevProject ? -90 : 0)) + 'deg) ;' : ''}
        {params.project == i ? 'transform-origin: center ' + swipeDirection + ';' : ''}{nextProject == i ? 'transform-origin: center left; ' : ' '}{prevProject == i ? 'transform-origin: center right; ' : ' '}{!held ? 'transition: left .5s ease, transform .5s ease; ' : 'transition: left 0s, transform 0s '}">
        <!-- {(i == prevProject ? 'translateX(-100%) ' : '')} {(i == nextProject ? 'translateX(100%) ' : '')} -->

        {#if params.project == i}
          <!-- if it's the current project -->
          <div id="indicators">
            {#each stories as story, p}
              {#if params.story > p}
                <div />
              {:else if params.story == p}
                <div id="currentIndicator">
                  <div
                    id="loadingBar"
                    class={held || navOpen || isPaused || bottomSheetOpen ? 'paused' : 'no'} />
                </div>
              {:else}
                <div class="nextIndicators" />
              {/if}
            {/each}
          </div>
          <a href="https://x.com/keshavchan" class="profile-info" target="_blank" rel="noopener noreferrer">
            <img src="./images/profile-pic" alt="Keshav" class="profile-pic" />
            <div class="profile-name-badge">
              <span class="profile-name">keshav</span>
              <svg class="verified-badge" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clip-path="url(#clip0_7285_5)">
                  <path d="M4.89062 0.975586C5.48567 0.322339 6.51339 0.322284 7.1084 0.975586L7.61816 1.53516C7.71834 1.645 7.86308 1.70519 8.01172 1.69824L8.76855 1.66309C9.65092 1.62224 10.3782 2.34894 10.3369 3.23145L10.3008 3.98828C10.294 4.13676 10.354 4.28075 10.4639 4.38086L11.0244 4.8916C11.677 5.48669 11.6774 6.51454 11.0244 7.10938L10.4639 7.61914C10.3538 7.71933 10.2938 7.86393 10.3008 8.0127L10.3369 8.76953C10.3777 9.65164 9.65066 10.3787 8.76855 10.3379L8.01172 10.3018C7.86296 10.2948 7.71835 10.3548 7.61816 10.4648L7.1084 11.0254C6.51356 11.6784 5.48572 11.678 4.89062 11.0254L4.37988 10.4648C4.27977 10.355 4.13578 10.295 3.9873 10.3018L3.23047 10.3379C2.34797 10.3791 1.62127 9.6519 1.66211 8.76953L1.69727 8.0127C1.70421 7.86406 1.64402 7.71932 1.53418 7.61914L0.974609 7.10938C0.321307 6.51437 0.321362 5.48665 0.974609 4.8916L1.53418 4.38086C1.64411 4.28074 1.70405 4.1368 1.69727 3.98828L1.66211 3.23145C1.62085 2.34872 2.34774 1.62183 3.23047 1.66309L3.9873 1.69824C4.13582 1.70503 4.27976 1.64509 4.37988 1.53516L4.89062 0.975586ZM7.85352 4.64648C7.65825 4.45122 7.34175 4.45122 7.14648 4.64648L5.5 6.29297L4.85352 5.64648C4.65825 5.45122 4.34175 5.45122 4.14648 5.64648C3.95122 5.84175 3.95122 6.15825 4.14648 6.35352L5.14648 7.35352C5.34177 7.5485 5.65835 7.54868 5.85352 7.35352L7.85352 5.35352C8.04868 5.15835 8.0485 4.84177 7.85352 4.64648Z" fill="white"/>
                </g>
                <defs>
                  <clipPath id="clip0_7285_5">
                    <rect width="12" height="12" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
            </div>
          </a>
          <button 
            class="play-pause-btn" 
            on:click|stopPropagation={togglePause}
            on:mousedown|stopPropagation
            on:mouseup|stopPropagation
            on:touchstart|stopPropagation
            on:touchend|stopPropagation
            aria-label={isPaused ? 'Play' : 'Pause'}>
            {#if isPaused}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.90588 4.53682C6.50592 4.2998 6 4.58808 6 5.05299V18.947C6 19.4119 6.50592 19.7002 6.90588 19.4632L18.629 12.5162C19.0211 12.2838 19.0211 11.7162 18.629 11.4838L6.90588 4.53682Z" fill="white" stroke="white" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"></path>
              </svg>
            {:else}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 18.4V5.6C6 5.26863 6.26863 5 6.6 5H9.4C9.73137 5 10 5.26863 10 5.6V18.4C10 18.7314 9.73137 19 9.4 19H6.6C6.26863 19 6 18.7314 6 18.4Z" fill="white" stroke="white" stroke-width="1.25"></path>
                <path d="M14 18.4V5.6C14 5.26863 14.2686 5 14.6 5H17.4C17.7314 5 18 5.26863 18 5.6V18.4C18 18.7314 17.7314 19 17.4 19H14.6C14.2686 19 14 18.7314 14 18.4Z" fill="white" stroke="white" stroke-width="1.25"></path>
              </svg>
            {/if}
          </button>
        {/if}

        {#each stories as story, j}
          <!-- each story -->
          <Story
            storyContent={story}
            current={params.project == i && params.story == j ? true : false}
            next={next.project == i && next.story == j ? true : false}
            prev={prev.project == i && prev.story == j ? true : false}
            nextCover={i == nextProject ? (j == activeStories[nextProject] ? true : false) : i == prevProject ? (j == activeStories[prevProject] ? true : false) : false} />
        {/each}
      </div>
      <!-- close project div -->
    {/each}
    <!-- //close 3d div -->

  </main>
</div>

<!-- Custom Bottom Sheet -->
{#if bottomSheetOpen && activeSheet}
  <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
  <div 
    class="video-modal-overlay" 
    on:click={closeBottomSheet}
    on:keydown={(e) => e.key === 'Escape' && closeBottomSheet()}
    role="dialog"
    aria-modal="true"
    tabindex="-1"
  >
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <div class="video-bottomsheet" on:click|stopPropagation role="document">
      <div class="bottomsheet-header">
        <button class="bottomsheet-back" on:click={closeBottomSheet} aria-label="Back">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 6L9 12L15 18" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
      {#if sheetLoading}
        <div class="sheet-loading">
          <div class="loading-spinner"></div>
        </div>
      {/if}
      <div class="sheet-content" class:single-item={activeSheet.items.length === 1} class:loading={sheetLoading}>
        {#each activeSheet.items as item, i}
          <div class="sheet-item">
            <div class="sheet-item-header">
              {#if activeSheet.items.length > 1}
                <span class="sheet-number">{i + 1}</span>
              {/if}
              {#if item.caption}
                <p class="sheet-caption">{item.caption}</p>
              {/if}
            </div>
            {#if item.type === 'video'}
              <div class="video-container" on:mousemove={() => handleVideoMouseMove(i)} on:mouseleave={() => handleVideoMouseLeave(i)}>
                <!-- svelte-ignore a11y-media-has-caption -->
                <!-- svelte-ignore a11y-click-events-have-key-events -->
                <video 
                  bind:this={sheetVideoElements[i]}
                  loop
                  playsinline
                  webkit-playsinline="true"
                  preload="metadata"
                  poster={item.src.replace('.mp4', '_poster.jpeg')}
                  class="sheet-video"
                  on:loadedmetadata={onAssetLoad}
                  on:seeked={() => updateVideoProgressOnce(i)}
                  on:play={() => { videoPlaying[i] = true; videoPlaying = [...videoPlaying]; startProgressAnimation(i); }}
                  on:pause={() => { videoPlaying[i] = false; videoPlaying = [...videoPlaying]; stopProgressAnimation(i); updateVideoProgressOnce(i); }}
                  on:click={() => toggleVideoPlayPause(i)}
                >
                  <source src={item.src} type="video/mp4"/>
                  Your browser does not support the video tag.
                </video>
                <div class="video-controls" class:hidden={controlsVisible[i] === false}>
                  <button class="video-play-btn" on:click={() => toggleVideoPlayPause(i)} aria-label={videoPlaying[i] ? 'Pause' : 'Play'}>
                    {#if videoPlaying[i]}
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 18.4V5.6C6 5.26863 6.26863 5 6.6 5H9.4C9.73137 5 10 5.26863 10 5.6V18.4C10 18.7314 9.73137 19 9.4 19H6.6C6.26863 19 6 18.7314 6 18.4Z" fill="white" stroke="white" stroke-width="1.25"></path>
                        <path d="M14 18.4V5.6C14 5.26863 14.2686 5 14.6 5H17.4C17.7314 5 18 5.26863 18 5.6V18.4C18 18.7314 17.7314 19 17.4 19H14.6C14.2686 19 14 18.7314 14 18.4Z" fill="white" stroke="white" stroke-width="1.25"></path>
                      </svg>
                    {:else}
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6.90588 4.53682C6.50592 4.2998 6 4.58808 6 5.05299V18.947C6 19.4119 6.50592 19.7002 6.90588 19.4632L18.629 12.5162C19.0211 12.2838 19.0211 11.7162 18.629 11.4838L6.90588 4.53682Z" fill="white" stroke="white" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"></path>
                      </svg>
                    {/if}
                  </button>
                  <div class="video-progress" on:click={(e) => seekVideo(i, e)}>
                    <div class="video-progress-bar" style="width: {videoProgress[i] || 0}%"></div>
                  </div>
                  <button class="video-fullscreen-btn" on:click={() => toggleFullscreen(i)} aria-label="Fullscreen">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15 9L20 4M20 4V8M20 4H16" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M9 15L4 20M4 20V16M4 20H8" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>
            {:else if item.type === 'image'}
              <img class="sheet-image" src={item.src} alt={item.alt || ''} on:load={onAssetLoad} />
            {/if}
          </div>
        {/each}
      </div>
    </div>
  </div>
{/if}
